/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction, Slice } from '@reduxjs/toolkit';
import { OperationStatus } from '../../types';
import {
  initialState,
  DeviceTelemetryInitialState,
  PostDeviceTelemetryPayload,
  DeviceTelemetryRequest
} from '../../types/device-telemetry';

import ApiError from '../../types/error';

const deviceTelemetrySlice = createSlice({
  name: 'device-telemetry',
  initialState,
  reducers: {
    // Initiates telemetry data collection by sending a POST request to the device
    postDeviceTelemetryRequest: (
      state: DeviceTelemetryInitialState,
      action: PayloadAction<PostDeviceTelemetryPayload>
    ) => {
      state.postDeviceTelemetry[`${action.payload.device.device_serial_nr}`] = {
        error: null,
        status: OperationStatus.RUNNING
      };
    },
    postDeviceTelemetrySuccess: (
      state: DeviceTelemetryInitialState,
      action: PayloadAction<{
        id: string;
        response: DeviceTelemetryRequest;
      }>
    ) => {
      const { id } = action.payload;

      state.postDeviceTelemetry[id] = {
        error: null,
        status: OperationStatus.COMPLETED
      };
    },
    postDeviceTelemetryFailure: (
      state: DeviceTelemetryInitialState,
      action: PayloadAction<{ id: string; error: ApiError }>
    ) => {
      state.postDeviceTelemetry[action.payload.id] = {
        error: action.payload.error,
        status: OperationStatus.ERRORED
      };
    },
    postDeviceTelemetryCleanup: (state) => {
      state.postDeviceTelemetry = initialState.postDeviceTelemetry;
    },

    // Retrieves active telemetry data for all connected devices
    getDeviceTelemetryRequest: (
      state: DeviceTelemetryInitialState,
      action: PayloadAction
    ) => {
      state.getDeviceTelemetry = {
        error: null,
        status: OperationStatus.RUNNING
      };
    },
    getDeviceTelemetrySuccess: (
      state: DeviceTelemetryInitialState,
      action: PayloadAction<DeviceTelemetryRequest[]>
    ) => {
      state.getDeviceTelemetry = {
        error: null,
        status: OperationStatus.COMPLETED
      };
      // Initialize the deviceTelemetry object if it doesn't exist
      if (!state.deviceTelemetry) {
        state.deviceTelemetry = {};
      }

      // Loop through the telemetry data and store it in the state
      action.payload.forEach((telemetry) => {
        const {
          device: { device_type, device_serial_nr },
          id,
          status
        } = telemetry;

        // Initialize nested structure if needed
        if (!state.deviceTelemetry[device_type]) {
          state.deviceTelemetry[device_type] = {};
        }
        if (!state.deviceTelemetry[device_type][device_serial_nr]) {
          state.deviceTelemetry[device_type][device_serial_nr] = {};
        }

        // Initialize the status array if it doesn't exist
        state.deviceTelemetry[device_type][device_serial_nr][status] =
          state.deviceTelemetry[device_type][device_serial_nr][status] || [];

        // Check if telemetry with this ID already exists to prevent duplicates
        const existingTelemetry =
          state.deviceTelemetry[device_type][device_serial_nr][status];
        const isDuplicate = existingTelemetry.some(
          (existing) => existing.id === id
        );

        if (!isDuplicate) {
          state.deviceTelemetry[device_type][device_serial_nr][status].push(
            telemetry
          );
        }
      });
    },
    getDeviceTelemetryFailure: (
      state: DeviceTelemetryInitialState,
      action: PayloadAction<ApiError>
    ) => {
      state.getDeviceTelemetry = {
        error: action.payload,
        status: OperationStatus.ERRORED
      };
    },
    getDeviceTelemetryCleanup: (state: DeviceTelemetryInitialState) => {
      state.getDeviceTelemetry = initialState.getDeviceTelemetry;
      state.deviceTelemetry = initialState.deviceTelemetry;
    }
  }
});

export const {
  postDeviceTelemetryRequest,
  postDeviceTelemetrySuccess,
  postDeviceTelemetryFailure,
  postDeviceTelemetryCleanup,
  getDeviceTelemetryRequest,
  getDeviceTelemetrySuccess,
  getDeviceTelemetryFailure,
  getDeviceTelemetryCleanup
} = deviceTelemetrySlice.actions;

const deviceTelemetrySliceExport: Slice<DeviceTelemetryInitialState> =
  deviceTelemetrySlice;
export default deviceTelemetrySliceExport;
